/**
 * The Business Unit Groups screen (f4.1) -- the top of the GFT US hierarchy.
 *
 * This replaces f1.1's phase placeholder behind the `business-unit-groups`
 * navigation key. f1.3's role-to-navigation mapping is NOT edited: f3.1
 * called its narrowing *"the third and last such edit"*, and this feature does
 * not make a fourth.
 *
 * **What the prototype draws that is deliberately absent**, so a reviewer can
 * check the list rather than file seven defects:
 *
 * | Prototype element | Why it is not here | Owner |
 * | --- | --- | --- |
 * | `Business Units` count column and the unit names in the row subtitle | `business_unit` has no table | f5.1 |
 * | `Clients` count column | `client` has no table | f6.1 |
 * | The row link through to Business Units | No target screen exists; the name links to this group's own detail instead | f5.1 |
 * | `Export` | Dropped. No requirement names this screen, and f1.5 refused an audit export on the same ground | -- |
 * | The six seeded groups | adr-16 forbids seeding hierarchy; the table ships empty | f8 |
 *
 * **f4.2 delivered the `DEM` and `CEM` columns and the two owner filters.**
 * The columns are populated from `current_owners`, which the server resolves
 * for every group in ONE query; the filters run **client-side**, because
 * f4.1's collection is unpaginated by decision and holds six rows -- adding
 * `dem_id`/`cem_id` query parameters would breach f4.1's explicit *"no
 * operation accepts a pagination, search, filter or sort parameter"* in order
 * to filter a list the client already holds in full.
 *
 * Every value on screen is bound to the server's answer. Nothing here derives
 * a status label, a permission or a visibility rule in the browser.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Ban,
  Layers,
  Pencil,
  Plus,
  RotateCcw,
  RotateCw,
  Search,
  ShieldAlert,
} from 'lucide-react';

import type { BusinessUnitGroup } from '@/api/client';
import {
  useBusinessUnitGroups,
  useLookupCatalog,
  useReactivateBusinessUnitGroup,
} from '@/api/queries';
import { useSessionScope } from '@/session/SessionScopeContext';
import { CREATE_GROUP, EDIT_NODE, rowCan } from '@/session/permissions';
import { ScopedEmptyState } from '@/ui/ScopedEmptyState';
import { SortableHeader } from '@/ui/SortableHeader';
import { nextSort, type SortState } from '@/ui/tableSort';

import { DeactivateGroupDialog } from './DeactivateGroupDialog';
import { GroupDialog } from './GroupDialog';
import {
  DEFAULT_SORT,
  badgeClassFor,
  initialsOf,
  isActive,
  isRefusal,
  visibleGroups,
  type SortColumn,
} from './groupView';
import {
  DELIVERY_ROLE_TYPE,
  GROUP_ROLE_CODES,
  NO_OWNER,
  applyOwnerFilters,
  ownerFor,
  ownerOptions,
  type GroupRoleCode,
} from './ownershipView';

function PageHead({ onCreate }: { onCreate?: () => void }) {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-title">Business Unit Groups</h1>
        <div className="page-sub">
          Top level of the GFT US hierarchy; Business Units hang off a group
        </div>
      </div>
      <div className="page-actions">
        {/* Rendered from the server's capability keys, never from a role code.
            The server remains the actual boundary on every write route. */}
        {onCreate && (
          <button type="button" className="btn btn-primary" onClick={onCreate}>
            <Plus aria-hidden="true" /> New group
          </button>
        )}
      </div>
    </div>
  );
}

function StateCard({
  icon,
  warning = false,
  title,
  children,
}: {
  icon: ReactNode;
  warning?: boolean;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="empty">
          <div className={`e-icon${warning ? ' e-icon-warning' : ''}`}>{icon}</div>
          <h3>{title}</h3>
          {children}
        </div>
      </div>
    </div>
  );
}

function NoAccess() {
  return (
    <>
      <PageHead />
      <StateCard warning icon={<ShieldAlert aria-hidden="true" />} title="No access">
        <p>
          The server refuses this area for your account, and records the refusal in the
          audit log.
        </p>
      </StateCard>
    </>
  );
}

/** Bars, not an empty table: distinguishable from a list with no rows. */
function LoadingSkeleton() {
  return (
    <>
      <PageHead />
      <div className="card" aria-busy="true" aria-label="Loading Business Unit Groups">
        <div className="card-body">
          {[0, 1, 2, 3, 4].map((index) => (
            <span key={index} className="skeleton-bar group-skeleton-line" />
          ))}
        </div>
      </div>
    </>
  );
}

/**
 * f9.2: the route is guarded by `RequirePermittedNavigation` in `App.tsx`, so
 * this screen no longer repeats the navigation check. Presentation only, as
 * ever: every endpoint below authorizes on its own authority.
 */
export function BusinessUnitGroupsPage() {
  const groups = useBusinessUnitGroups();
  // The SAME hook the Reference data and Users screens use. No second fetch of
  // the same endpoint, and no statuses endpoint of its own.
  const catalog = useLookupCatalog();
  const { can } = useSessionScope();
  // SESSION-level: only a caller the server lets create a group sees
  // `New group` (Q-f9.2-5). Each row's controls come from its own `permissions`.
  const mayCreateGroup = can(CREATE_GROUP);

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState<SortColumn>>(DEFAULT_SORT);
  const [editing, setEditing] = useState<BusinessUnitGroup | null>(null);
  const [creating, setCreating] = useState(false);
  const [deactivating, setDeactivating] = useState<BusinessUnitGroup | null>(null);
  /* The prototype's `All DEMs` / `All CEMs` selects. Client-side, over the
     collection the server already returned whole. */
  const [owners, setOwners] = useState<Partial<Record<GroupRoleCode, string>>>({});

  const deliveryRoles = useMemo(
    () => catalog.data?.types.find((type) => type.code === DELIVERY_ROLE_TYPE)?.values ?? [],
    [catalog.data],
  );

  const all = groups.data?.business_unit_groups ?? [];
  const visible = useMemo(
    () => visibleGroups(applyOwnerFilters(all, owners), { search, sort }),
    [all, owners, search, sort],
  );

  if (groups.isPending || catalog.isPending) return <LoadingSkeleton />;

  const failure = groups.error ?? catalog.error;
  if (failure) {
    // A 403 is a refusal, not a failure -- and it never triggers the
    // interactive re-authentication a 401 does (f1.2).
    if (isRefusal(failure)) return <NoAccess />;
    return (
      <>
        <PageHead />
        <StateCard
          warning
          icon={<AlertTriangle aria-hidden="true" />}
          title="The Business Unit Groups could not be loaded"
        >
          <p>{failure instanceof Error ? failure.message : 'Unexpected error.'}</p>
          <p className="empty-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                void groups.refetch();
                void catalog.refetch();
              }}
            >
              <RotateCw aria-hidden="true" /> Retry
            </button>
          </p>
        </StateCard>
      </>
    );
  }

  const toggleSort = (column: SortColumn) =>
    setSort((current) => nextSort(current, column));

  return (
    <>
      <PageHead onCreate={mayCreateGroup ? () => setCreating(true) : undefined} />

      <div className="toolbar">
        <div className="search-field">
          <Search aria-hidden="true" />
          <input
            type="search"
            value={search}
            placeholder="Search group or code"
            aria-label="Search group or code"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        {/* Options are the distinct current holders actually present, not
            every assignable account: a filter offering names that match no
            row is a worse control than one offering the names that do. */}
        {GROUP_ROLE_CODES.map((roleCode) => (
          <OwnerFilter
            key={roleCode}
            roleCode={roleCode}
            groups={all}
            chosen={owners[roleCode] ?? ''}
            onChange={(userId) =>
              setOwners((current) => ({ ...current, [roleCode]: userId }))
            }
          />
        ))}
      </div>

      <div className="card">
        <div className="table-scroll">
          <table className="data zebra">
            <thead>
              {/* Every column sorts except the last one, which holds the row
                  actions and has nothing to order by. */}
              <tr>
                <SortableHeader column="code" label="Code" sort={sort} onSort={toggleSort} />
                <SortableHeader
                  column="name"
                  label="Business Unit Group"
                  sort={sort}
                  onSort={toggleSort}
                />
                {/* f5.1 delivered the count f4.1 deferred. No query parameter
                    was added to make it possible: it is a response field. */}
                <SortableHeader
                  column="units"
                  label="Business Units"
                  sort={sort}
                  onSort={toggleSort}
                  className="num"
                />
                {GROUP_ROLE_CODES.map((roleCode) => (
                  <SortableHeader
                    key={roleCode}
                    column={roleCode}
                    label={roleCode}
                    sort={sort}
                    onSort={toggleSort}
                  />
                ))}
                <SortableHeader column="status" label="Status" sort={sort} onSort={toggleSort} />
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={5 + GROUP_ROLE_CODES.length}>
                    <ScopedEmptyState
                      searching={all.length > 0}
                      matchNoun="groups"
                      collectionNoun="Business Unit Groups"
                      icon={<Layers aria-hidden="true" />}
                      freshInstallHint="Create the first group, or wait for the master workbook import to bring the hierarchy across."
                    />
                  </td>
                </tr>
              ) : (
                visible.map((row) => (
                  <GroupRow
                    key={row.id}
                    group={row}
                    onEdit={() => setEditing(row)}
                    onDeactivate={() => setDeactivating(row)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Not a disclosure: `all` is the collection the server ALREADY scoped,
          and `visible` is what the caller's own search and filters left of it.
          It reports the caller's narrowing, never the server's -- so it stays,
          and no "N of M" against an unscoped total is ever composed here. */}
      <div className="pagination">
        <span className="info">
          {visible.length === all.length
            ? `Showing all ${all.length} Business Unit ${all.length === 1 ? 'Group' : 'Groups'}`
            : `Showing ${visible.length} of ${all.length} Business Unit Groups`}
        </span>
      </div>

      {(creating || editing) && (
        <GroupDialog
          group={editing}
          deliveryRoles={deliveryRoles}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
      {deactivating && (
        <DeactivateGroupDialog
          group={deactivating}
          onClose={() => setDeactivating(null)}
        />
      )}
    </>
  );
}

function GroupRow({
  group,
  onEdit,
  onDeactivate,
}: {
  group: BusinessUnitGroup;
  onEdit: () => void;
  onDeactivate: () => void;
}) {
  const reactivate = useReactivateBusinessUnitGroup();
  const busy = reactivate.isPending;
  const active = isActive(group);
  // The ROW's own capability, projected by the server from f9.1's grant table.
  const mayEdit = rowCan(group, EDIT_NODE);

  return (
    <tr>
      <td className="mono">{group.code}</td>
      <td>
        <Link className="cell-link" to={`/business-unit-groups/${group.id}`}>
          {group.name}
        </Link>
      </td>
      <td className="num">
        {/* f5.1's two deferrals, discharged together: the count, and the row
            link through to the Business Units screen filtered to this group.
            The filter is applied CLIENT-side there -- the collection takes no
            parameter -- so the link carries the group in the query string and
            the screen reads it. */}
        {group.business_unit_count === 0 ? (
          <span className="hint" title="No active Business Units under this group yet.">
            0
          </span>
        ) : (
          <Link
            className="cell-link"
            to={`/business-units?group=${encodeURIComponent(group.id)}`}
          >
            {group.business_unit_count}
          </Link>
        )}
      </td>
      {GROUP_ROLE_CODES.map((roleCode) => (
        <td key={roleCode}>
          <OwnerCell group={group} roleCode={roleCode} />
        </td>
      ))}
      <td>
        {/* The LABEL is the server's; only the class is chosen here. */}
        <span className={badgeClassFor(group.status.code)}>{group.status.name}</span>
      </td>
      <td>
        <div className="row-actions">
          {mayEdit && (
            <>
              <button
                type="button"
                className="btn btn-secondary btn-icon"
                title="Edit"
                aria-label={`Edit ${group.name}`}
                disabled={busy}
                onClick={onEdit}
              >
                <Pencil aria-hidden="true" />
              </button>
              {active ? (
                <button
                  type="button"
                  className="btn btn-secondary btn-icon"
                  title="Deactivate"
                  aria-label={`Deactivate ${group.name}`}
                  disabled={busy}
                  onClick={onDeactivate}
                >
                  <Ban aria-hidden="true" />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-secondary btn-icon"
                  title="Reactivate"
                  aria-label={`Reactivate ${group.name}`}
                  disabled={busy}
                  // Unguarded and unconfirmed: reactivating a parent cannot
                  // orphan anything, and it grants nothing new.
                  onClick={() => reactivate.mutate(group.id)}
                >
                  <RotateCcw aria-hidden="true" />
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

/**
 * One owner cell.
 *
 * A group with no owner renders the empty placeholder, never a blank cell and never a
 * fabricated name. The prototype's four names are a mockup: `CU CITI`'s
 * legacy DEM is `Tomasz Różanek`, not the `Axel Apice` the mockup draws.
 */
function OwnerCell({
  group,
  roleCode,
}: {
  group: BusinessUnitGroup;
  roleCode: GroupRoleCode;
}) {
  const owner = ownerFor(group, roleCode);
  if (!owner) return <span className="hint">{NO_OWNER}</span>;
  return (
    <span className="row owner-cell">
      <span className="avatar sm" aria-hidden="true">
        {initialsOf(owner.display_name)}
      </span>
      {owner.display_name}
    </span>
  );
}

/** One of the prototype's two owner selects, filtering client-side. */
function OwnerFilter({
  roleCode,
  groups,
  chosen,
  onChange,
}: {
  roleCode: GroupRoleCode;
  groups: readonly BusinessUnitGroup[];
  chosen: string;
  onChange: (userId: string) => void;
}) {
  const options = useMemo(() => ownerOptions(groups, roleCode), [groups, roleCode]);
  return (
    <select
      className="toolbar-select"
      aria-label={`Filter by ${roleCode}`}
      value={chosen}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">All {roleCode}s</option>
      {options.map((option) => (
        <option key={option.userId} value={option.userId}>
          {option.displayName}
        </option>
      ))}
    </select>
  );
}
